import { useState } from 'react';
import { 
  TrendingDown, 
  TrendingUp, 
  Building2, 
  Percent,
  Calendar,
  ArrowRight,
  CheckCircle2,
  Calculator as CalcIcon
} from 'lucide-react';

// Format currency in Crores with 2 decimal places
const formatCrores = (value) => {
  const crores = value / 10000000;
  return `₹${crores.toFixed(2)} Cr`;
};

// Format currency in Lakhs
const formatLakhs = (value) => {
  const lakhs = value / 100000;
  return `₹${lakhs.toFixed(2)} L`;
};

// Format monthly value
const formatMonthly = (value) => {
  if (value >= 10000000) {
    return `₹${(value / 10000000).toFixed(2)} Cr/month`;
  } else if (value >= 100000) {
    return `₹${(value / 100000).toFixed(2)} L/month`;
  }
  return `₹${Math.round(value).toLocaleString('en-IN')}/month`;
};

export const CapexCalculator = () => {
  // User inputs
  const [interestRate, setInterestRate] = useState(12); // 10%, 12%, or 14%
  const [horizonMonths, setHorizonMonths] = useState(42); // 42, 48, or 60 months
  
  // Fixed Business Assumptions (from spec)
  const WAREHOUSE_SIZE = 100000; // sft
  const STANDARD_CAPEX = 35000000; // ₹3.5 Crore per 100,000 sft
  const WAREHAUS_RECOVERY_PERIOD = 42; // months
  const MAINTENANCE_RATE = 0.03; // 3% per annum
  const RELOCATION_RATE = 0.10; // 10% of CAPEX at month 36
  
  // Step 1: Compute CAPEX Amount
  const capex = STANDARD_CAPEX;
  
  // Step 2: Compute Warehaus+ Monthly Rental (OPEX)
  const opexMonthly = capex / WAREHAUS_RECOVERY_PERIOD;
  
  // Step 3: Compute CAPEX Financing (EMI)
  const monthlyInterestRate = interestRate / 100 / 12;
  const n = horizonMonths;
  
  // EMI Formula: P × r × (1+r)^n / ((1+r)^n − 1)
  const emiNumerator = capex * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, n);
  const emiDenominator = Math.pow(1 + monthlyInterestRate, n) - 1;
  const emi = emiNumerator / emiDenominator;
  const totalEmiOutflow = emi * n;
  
  // Step 4: Maintenance Cost
  const annualMaintenance = MAINTENANCE_RATE * capex;
  const maintenanceTotal = annualMaintenance * (horizonMonths / 12);
  
  // Step 5: Relocation Cost (applies if horizon >= 36 months)
  const relocationTotal = horizonMonths >= 36 ? RELOCATION_RATE * capex : 0;
  
  // Step 6: Total CAPEX Cash Outflow
  const capexTotal = totalEmiOutflow + maintenanceTotal + relocationTotal;
  
  // Step 7: Total OPEX Cash Outflow
  const opexTotal = opexMonthly * horizonMonths;
  
  // Savings calculation
  const savings = capexTotal - opexTotal;
  const savingsPercentage = (savings / capexTotal) * 100;
  
  return (
    <section className="py-20 md:py-28 bg-[#0c1e35] relative overflow-hidden" data-testid="capex-calculator">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#E63946] opacity-[0.02] blur-[150px] rounded-full" />
      
      <div className="container-custom relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="font-mono text-xs text-[#E63946] uppercase tracking-[0.2em] mb-4 block">
            // Interactive Calculator
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-4">
            CAPEX vs <span className="text-[#E63946]">OPEX</span> Comparison
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Compare traditional CAPEX investment with Warehaus+ flexible OPEX rental model. 
            See how much you can save with our rental-first approach.
          </p>
        </div>

        {/* Calculator Card */}
        <div className="max-w-5xl mx-auto">
          <div className="industrial-card bg-[#0a1628] border border-white/10 p-8 md:p-10">
            
            {/* Fixed Assumptions Banner */}
            <div className="bg-[#E63946]/10 border border-[#E63946]/20 p-4 mb-8 rounded">
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-5 h-5 text-[#E63946]" />
                <span className="font-heading text-sm font-bold text-white uppercase tracking-wider">
                  Base Case Assumptions
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Warehouse Size:</span>
                  <span className="text-white ml-2 font-mono">1,00,000 sft</span>
                </div>
                <div>
                  <span className="text-gray-500">Standard CAPEX:</span>
                  <span className="text-white ml-2 font-mono">₹3.5 Cr</span>
                </div>
                <div>
                  <span className="text-gray-500">Maintenance:</span>
                  <span className="text-white ml-2 font-mono">3% p.a.</span>
                </div>
                <div>
                  <span className="text-gray-500">Relocation:</span>
                  <span className="text-white ml-2 font-mono">10% at M36</span>
                </div>
              </div>
            </div>

            {/* User Input Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              {/* Interest Rate Selector */}
              <div>
                <label className="block text-sm text-gray-400 mb-3 uppercase tracking-wider">
                  <Percent className="w-4 h-4 inline mr-2 text-[#E63946]" />
                  Cost of Capital / Interest Rate
                </label>
                <div className="flex gap-3">
                  {[10, 12, 14].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setInterestRate(rate)}
                      className={`flex-1 py-3 px-4 font-mono text-lg font-bold transition-all ${
                        interestRate === rate
                          ? 'bg-[#E63946] text-white'
                          : 'bg-[#0f1e32] border border-white/10 text-gray-400 hover:border-[#E63946]/50'
                      }`}
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Comparison Horizon Selector */}
              <div>
                <label className="block text-sm text-gray-400 mb-3 uppercase tracking-wider">
                  <Calendar className="w-4 h-4 inline mr-2 text-[#E63946]" />
                  Comparison Horizon
                </label>
                <div className="flex gap-3">
                  {[42, 48, 60].map((months) => (
                    <button
                      key={months}
                      onClick={() => setHorizonMonths(months)}
                      className={`flex-1 py-3 px-4 font-mono text-lg font-bold transition-all ${
                        horizonMonths === months
                          ? 'bg-[#E63946] text-white'
                          : 'bg-[#0f1e32] border border-white/10 text-gray-400 hover:border-[#E63946]/50'
                      }`}
                    >
                      {months} mo
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results Comparison */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
              {/* CAPEX Column */}
              <div className="bg-[#0f1e32] border border-white/10 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading text-xl font-bold text-white uppercase">
                    Traditional CAPEX
                  </h3>
                  <div className="bg-red-500/20 px-3 py-1 rounded">
                    <span className="text-red-400 text-xs font-mono uppercase">Higher Cost</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-gray-400 text-sm">Monthly EMI</span>
                    <span className="font-mono text-lg text-white">{formatMonthly(emi)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-gray-400 text-sm">Total EMI Outflow</span>
                    <span className="font-mono text-white">{formatCrores(totalEmiOutflow)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-gray-400 text-sm">Maintenance ({horizonMonths} mo)</span>
                    <span className="font-mono text-white">{formatLakhs(maintenanceTotal)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-gray-400 text-sm">Relocation Cost</span>
                    <span className="font-mono text-white">{horizonMonths >= 36 ? formatLakhs(relocationTotal) : '₹0'}</span>
                  </div>
                  <div className="flex justify-between items-center pt-4">
                    <span className="text-white font-bold uppercase text-sm">Total Cash Outflow</span>
                    <span className="font-mono text-2xl text-red-400 font-bold">{formatCrores(capexTotal)}</span>
                  </div>
                </div>
              </div>

              {/* OPEX Column */}
              <div className="bg-[#0f1e32] border-2 border-[#E63946]/30 p-6 relative">
                <div className="absolute -top-3 left-4 bg-[#E63946] px-3 py-1">
                  <span className="text-white text-xs font-bold uppercase tracking-wider">Recommended</span>
                </div>
                
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading text-xl font-bold text-white uppercase">
                    Warehaus+ OPEX
                  </h3>
                  <div className="bg-green-500/20 px-3 py-1 rounded">
                    <span className="text-green-400 text-xs font-mono uppercase">Lower Cost</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-gray-400 text-sm">Monthly Rental</span>
                    <span className="font-mono text-lg text-white">{formatMonthly(opexMonthly)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-gray-400 text-sm">Recovery Period</span>
                    <span className="font-mono text-white">42 months</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-gray-400 text-sm">Maintenance</span>
                    <span className="font-mono text-green-400">Included</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-gray-400 text-sm">Relocation Flexibility</span>
                    <span className="font-mono text-green-400">Full Flexibility</span>
                  </div>
                  <div className="flex justify-between items-center pt-4">
                    <span className="text-white font-bold uppercase text-sm">Total Cash Outflow</span>
                    <span className="font-mono text-2xl text-green-400 font-bold">{formatCrores(opexTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Savings Summary */}
            {savings > 0 && (
              <div className="bg-gradient-to-r from-[#E63946]/20 to-[#E63946]/5 border border-[#E63946]/30 p-6 text-center">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <TrendingDown className="w-6 h-6 text-[#E63946]" />
                  <span className="font-heading text-lg text-white uppercase tracking-wider">Your Savings with Warehaus+</span>
                </div>
                <div className="flex items-center justify-center gap-8">
                  <div>
                    <div className="font-mono text-4xl md:text-5xl font-bold text-[#E63946]">
                      {formatCrores(savings)}
                    </div>
                    <div className="text-gray-400 text-sm mt-1">Total Savings over {horizonMonths} months</div>
                  </div>
                  <div className="hidden md:block h-16 w-px bg-white/10" />
                  <div className="hidden md:block">
                    <div className="font-mono text-4xl md:text-5xl font-bold text-[#E63946]">
                      {savingsPercentage.toFixed(0)}%
                    </div>
                    <div className="text-gray-400 text-sm mt-1">Cost Reduction</div>
                  </div>
                </div>
              </div>
            )}

            {/* Benefits */}
            <div className="mt-10 pt-8 border-t border-white/10">
              <h4 className="font-heading text-lg font-bold text-white uppercase mb-6 text-center">
                Why Choose Warehaus+ OPEX Model?
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 p-4 bg-[#0f1e32] border border-white/5">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white text-sm font-medium">Zero Upfront Investment</p>
                    <p className="text-gray-500 text-xs mt-1">No large capital outlay required</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-[#0f1e32] border border-white/5">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white text-sm font-medium">Maintenance Included</p>
                    <p className="text-gray-500 text-xs mt-1">No additional maintenance costs</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-[#0f1e32] border border-white/5">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white text-sm font-medium">Full Flexibility</p>
                    <p className="text-gray-500 text-xs mt-1">Scale up or relocate easily</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 text-center">
              <p className="text-gray-600 text-xs">
                * Calculations based on standard assumptions. Actual costs may vary based on specific requirements. 
                Contact us for a customized quote.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CapexCalculator;
